import React, { useEffect, useState, useRef } from 'react';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../redux/store';
import { DBTimelineThunks } from '../../../redux/thunks/DBServiceThunks';
import { Timeline } from '../../../models/Timeline';

interface TimelineProgressProps {
  timeline: Timeline;
  onComplete?: () => void;
}

export default function TimelineProgress({ timeline, onComplete }: TimelineProgressProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [progress, setProgress] = useState(timeline.lastProgressUpdate?.progress || 0);
  const [stage, setStage] = useState(timeline.lastProgressUpdate?.stage || 'initialization');
  const [details, setDetails] = useState(timeline.lastProgressUpdate?.details || '');
  const [feedUpdates, setFeedUpdates] = useState<Array<{ id: string, message: string, timestamp: string }>>(
    timeline.recentFeedUpdates || []
  );
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastPollTimeRef = useRef<number>(0);
  const onCompleteRef = useRef(onComplete);

  // Keep onCompleteRef up to date
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Stage display names
  const stageLabels: Record<string, string> = {
    initialization: t('common.timeline.progress.initialization') || 'Initializing',
    document_processing: t('common.timeline.progress.documentProcessing') || 'Processing Documents',
    consolidation: t('common.timeline.progress.consolidation') || 'Consolidating Events',
    finalization: t('common.timeline.progress.finalization') || 'Finalizing',
    error: t('common.labels.error') || 'Error'
  };

  // Polling for progress updates
  useEffect(() => {
    // Only poll if status is generating
    if (timeline.status !== 'generating') {
      // Clear any existing interval
      if (pollingIntervalRef.current) {
        console.log('[TimelineProgress] Stopping polling - status is not generating');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const POLL_INTERVAL = 3000; // 3 seconds
    const MIN_POLL_GAP = 2500; // Minimum 2.5 seconds between polls

    const pollTimeline = async () => {
      const now = Date.now();
      const timeSinceLastPoll = now - lastPollTimeRef.current;

      // Enforce minimum interval
      if (timeSinceLastPoll < MIN_POLL_GAP) {
        console.log('[TimelineProgress] Skipping poll - too soon since last poll');
        return;
      }

      lastPollTimeRef.current = now;

      try {
        const result = await dispatch(DBTimelineThunks.find({ _id: timeline._id })).unwrap();
        const timelines = Array.isArray(result) ? result : result.data || [];
        const updatedTimeline = timelines[0];

        if (!updatedTimeline) return;

        // Update progress
        if (updatedTimeline.lastProgressUpdate) {
          setProgress(updatedTimeline.lastProgressUpdate.progress);
          setStage(updatedTimeline.lastProgressUpdate.stage);
          setDetails(updatedTimeline.lastProgressUpdate.details);
        }

        if (updatedTimeline.recentFeedUpdates) {
          setFeedUpdates(updatedTimeline.recentFeedUpdates);
        }

        // Check if completed
        if (updatedTimeline.status === 'completed') {
          console.log('[TimelineProgress] Timeline completed, stopping polling');
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (onCompleteRef.current) {
            setTimeout(() => onCompleteRef.current?.(), 1000);
          }
        }
      } catch (error) {
        console.error('[TimelineProgress] Polling error:', error);
      }
    };

    // Initial poll
    console.log('[TimelineProgress] Starting polling for timeline:', timeline._id);
    pollTimeline();

    // Set up interval
    pollingIntervalRef.current = setInterval(pollTimeline, POLL_INTERVAL);

    return () => {
      console.log('[TimelineProgress] Cleanup - clearing interval');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [timeline._id, timeline.status, dispatch]);

  const getStatusIcon = () => {
    if (timeline.status === 'completed') {
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    }
    if (timeline.status === 'error') {
      return <AlertCircle className="h-5 w-5 text-destructive" />;
    }
    return <Loader2 className="h-5 w-5 animate-spin text-gold" />;
  };

  return (
    <Card className="m-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {getStatusIcon()}
          <span>{stageLabels[stage] || stage}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{details}</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Feed updates */}
        {feedUpdates.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">{t('common.timeline.progress.activityFeed')}</h4>
            <ScrollArea className="h-48 rounded-md border p-3">
              <div className="space-y-2">
                {feedUpdates.map((feed) => (
                  <div
                    key={feed.id}
                    className="text-xs text-muted-foreground pb-2 border-b last:border-0 animate-fade-in"
                  >
                    <p>{feed.message}</p>
                    <span className="text-[10px] text-muted-foreground/60">
                      {new Date(feed.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Metrics */}
        {timeline.totalDocumentsProcessed > 0 && (
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 rounded-md bg-muted">
              <div className="font-semibold">{timeline.totalDocumentsProcessed}</div>
              <div className="text-muted-foreground">{t('common.timeline.progress.documentsLabel')}</div>
            </div>
            <div className="p-2 rounded-md bg-muted">
              <div className="font-semibold">{timeline.totalEventsExtracted}</div>
              <div className="text-muted-foreground">{t('common.timeline.progress.extractedLabel')}</div>
            </div>
            <div className="p-2 rounded-md bg-muted">
              <div className="font-semibold">{timeline.totalEventsConsolidated}</div>
              <div className="text-muted-foreground">{t('common.timeline.progress.eventsLabel')}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

